import { bindAll } from 'bind-event-listener';

/**
 * Shared, singleton observer of the browser's current location.
 *
 * Why this exists
 * ---------------
 * Several hooks across the chat experience need to re-derive when the SPA
 * navigates (`history.pushState` / `replaceState` / `popstate`). Historically
 * each hook patched `window.history` in its own `useEffect` and restored the
 * native methods on unmount. That is unsafe as soon as more than one such hook
 * is mounted in the same tree: the patches chain on mount, and the FIRST hook to
 * unmount restores *its* captured "original", silently unpatching every other
 * still-mounted hook. The result is a location value that goes stale after
 * client-side navigation (e.g. the context banner smart link not updating).
 *
 * The fix
 * -------
 * Patch `history.pushState` / `replaceState` exactly **once** for the lifetime of
 * the page and **never restore them**. This is the standard, safe pattern used by
 * SPA routers — `history` patching is process-global, so ownership must be
 * process-global too. Components subscribe/unsubscribe to be notified of changes;
 * unsubscribing only removes a listener and never touches `history`, so there is
 * nothing to clobber regardless of mount/unmount ordering.
 *
 * Document title
 * --------------
 * The same shell that navigates also updates `document.title` — but it does so
 * *asynchronously, after* the `pushState`/`popstate` that changes the location.
 * A render-time `document.title` read therefore lags one navigation behind. To
 * keep title-derived UI (e.g. the context banner's smart-link placeholder) fresh,
 * this observer also watches the `<title>` element with a `MutationObserver` and
 * notifies title subscribers when it changes.
 *
 * SSR
 * ---
 * No `window`/`history`/`document` access happens at module load. All access is
 * guarded and lazy, so importing this module on the server is safe; the snapshot
 * getters return an empty string when the DOM is unavailable.
 */
/* eslint-disable @atlaskit/platform/one-value-export-per-file -- This is a single external-store module: the `subscribe*`/`get*Snapshot` functions form one cohesive `useSyncExternalStore` contract and must live together to share the module-singleton state. */
/* eslint-disable @atlaskit/platform/no-direct-document-usage -- This observer is the single, deliberate owner of direct `document`/`<title>` access for the chat experience; all access is SSR-guarded via `hasDocument()`. Routing through `getDocument()` here would add a cross-workspace dependency for no behavioural benefit. */

type Listener = () => void;

const locationListeners = new Set<Listener>();
const titleListeners = new Set<Listener>();

let patched = false;
let currentHref = '';
let currentTitle = '';
let titleMutationObserver: MutationObserver | undefined;

const isBrowser = (): boolean => typeof window !== 'undefined';
const hasDocument = (): boolean => typeof document !== 'undefined';

const readHref = (): string => (isBrowser() ? window.location.href : '');
const readTitle = (): string => (hasDocument() ? document.title : '');

// Snapshot the set so a listener that (un)subscribes during notification does
// not mutate the collection we are iterating.
const emit = (set: Set<Listener>): void => {
	for (const listener of Array.from(set)) {
		listener();
	}
};

const notifyLocation = (): void => {
	const nextHref = readHref();
	if (nextHref === currentHref) {
		return;
	}
	currentHref = nextHref;
	emit(locationListeners);
};

const notifyTitle = (): void => {
	const nextTitle = readTitle();
	if (nextTitle === currentTitle) {
		return;
	}
	currentTitle = nextTitle;
	emit(titleListeners);
};

const installOnce = (): void => {
	if (patched || !isBrowser()) {
		return;
	}
	patched = true;
	currentHref = readHref();
	currentTitle = readTitle();

	const nativePushState = window.history.pushState.bind(window.history);
	const nativeReplaceState = window.history.replaceState.bind(window.history);

	// Intentionally permanent: we never restore these. See module docblock.
	window.history.pushState = function (...args) {
		nativePushState(...(args as Parameters<typeof nativePushState>));
		notifyLocation();
	};
	window.history.replaceState = function (...args) {
		nativeReplaceState(...(args as Parameters<typeof nativeReplaceState>));
		notifyLocation();
	};

	bindAll(window, [
		{
			type: 'popstate',
			listener: notifyLocation,
		},
		{
			type: 'hashchange',
			listener: notifyLocation,
		},
	]);

	// Watch the <title> element for changes. The shell updates `document.title`
	// after the location change, so this is what keeps title-derived UI fresh.
	if (hasDocument() && typeof MutationObserver !== 'undefined') {
		const titleEl = document.querySelector('title');
		if (titleEl) {
			titleMutationObserver = new MutationObserver(notifyTitle);
			titleMutationObserver.observe(titleEl, {
				childList: true,
				characterData: true,
				subtree: true,
			});
		}
	}
};

/**
 * Subscribe to location changes. Returns an unsubscribe function.
 *
 * Designed to be passed directly to `useSyncExternalStore`.
 */
export const subscribeToLocation = (listener: Listener): (() => void) => {
	installOnce();
	locationListeners.add(listener);
	return () => {
		locationListeners.delete(listener);
	};
};

/**
 * Subscribe to `document.title` changes. Returns an unsubscribe function.
 *
 * Designed to be passed directly to `useSyncExternalStore`.
 */
export const subscribeToTitle = (listener: Listener): (() => void) => {
	installOnce();
	titleListeners.add(listener);
	return () => {
		titleListeners.delete(listener);
	};
};

/**
 * Current location href. Cheap, synchronous, safe for `useSyncExternalStore`'s
 * `getSnapshot`. Returns `''` during SSR.
 *
 * Reads `window.location.href` directly (rather than a cached value) so the
 * snapshot is always current — including navigations that happen without a
 * `pushState`/`replaceState`/`popstate` event (e.g. tests that mutate
 * `window.location` directly, or a full-document load). `useSyncExternalStore`
 * still only triggers a re-render when a subscriber is notified, but the value
 * it reads at render time is never stale.
 */
export const getLocationSnapshot = (): string => readHref();

/**
 * Server snapshot for `useSyncExternalStore`. Always returns `''` so server and
 * initial client markup agree (the real href is reconciled after hydration).
 */
export const getLocationServerSnapshot = (): string => '';

/**
 * Current `document.title`. Cheap, synchronous, safe for `useSyncExternalStore`'s
 * `getSnapshot`. Returns `''` when the DOM is unavailable (SSR).
 */
export const getTitleSnapshot = (): string => readTitle();

/**
 * Server snapshot for `useSyncExternalStore`. Always returns `''` so server and
 * initial client markup agree (the real title is reconciled after hydration).
 */
export const getTitleServerSnapshot = (): string => '';

// --- test-only reset -------------------------------------------------------
// Allows unit tests to restore native history methods between cases. NOT used in
// production code paths.
export const __resetHistoryLocationObserverForTests = (): void => {
	patched = false;
	currentHref = '';
	currentTitle = '';
	locationListeners.clear();
	titleListeners.clear();
	titleMutationObserver?.disconnect();
	titleMutationObserver = undefined;
};
